import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { usePermission } from '@/lib/permissions'
import { DEFAULT_EVENT_TIMEZONE, eventLocalToServerNaive, instantToEventLocal, timeZoneLabel } from '@/lib/eventTime'
import { useIsDarkMode } from '@/lib/hooks/use-dark-mode'
import { AccessDenied } from '@/components/permission-guard'
import {
  useMapConfig,
  useUpdateMapConfig,
  useMapHeatmap,
  useMapTimeline,
  useEvent,
  useVendorEvents,
  useEventUsers,
} from '@/lib/api/queries'
import { apiClient } from '@/lib/api/client'
import { useAuth } from '@/lib/auth'
import type {
  MapConfig,
  HeatmapLocation,
  TimelineResponse,
  TimelineInterval,
  JourneyResponse,
  JourneyStep,
  VendorEvent,
} from '@/lib/api/types'
import mapboxgl from 'mapbox-gl'
import { FALLBACK_MAP_CENTER, cachedVenueCenter, geocodeVenueCenter, type LngLat } from '@/lib/venue-center'
import 'mapbox-gl/dist/mapbox-gl.css'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/lib/api/queries'
import {
  Map as MapIcon,
  Settings,
  Flame,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Upload,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Route as RouteIcon,
  X,
  Loader2,
  MapPin,
  Crosshair,
  Pen,
  Undo2,
  Search,
  ChevronDown,
} from 'lucide-react'

export const Route = createFileRoute('/_authenticated/events/$eventId/site-map')({
  component: SiteMapPage,
})

type ViewMode = 'setup' | 'heatmap' | 'timeline' | 'journey'

// Data that MapCanvas needs from the active panel to render overlays
interface MapVisualization {
  heatmapLocations?: HeatmapLocation[]
  timelineFrame?: TimelineInterval['locations']
  journeys?: Array<{ user_id: number; path: JourneyStep[]; color: string }>
  journeyPlaybackStep?: number // -1 or undefined = show all, 0+ = show up to this step index
}

// ────────────────────── Main Page ──────────────────────
function SiteMapPage() {
  const { eventId } = Route.useParams()
  const eventIdNum = Number(eventId)
  const isDark = useIsDarkMode()

  // Hooks first (rules of hooks)
  const canViewSiteMap = usePermission('can_view_site_map')
  const canEditEvents = usePermission('can_edit_events')

  const { data: event } = useEvent(eventIdNum)
  const { data: mapConfig, isLoading: configLoading } = useMapConfig(eventIdNum)
  const { data: vendorEventsData } = useVendorEvents(eventIdNum)

  // Tab state with URL hash
  const getInitialMode = (): ViewMode => {
    if (typeof window === 'undefined') return 'setup'
    const hash = window.location.hash.replace('#', '')
    if (hash === 'setup' || hash === 'heatmap' || hash === 'timeline' || hash === 'journey') return hash
    return 'setup'
  }
  const [mode, setMode] = useState<ViewMode>(getInitialMode)
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return true
    return window.matchMedia('(min-width: 1024px)').matches
  })

  // Sync tab with URL hash
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '') as ViewMode
      if (hash === 'setup' || hash === 'heatmap' || hash === 'timeline' || hash === 'journey') {
        setMode(hash)
      }
    }
    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])

  // Vendor placement state (lifted)
  const [selectedVendorId, setSelectedVendorId] = useState<number | null>(null)

  // Boundary drawing state (lifted)
  const [drawingBoundary, setDrawingBoundary] = useState(false)
  const [boundaryPoints, setBoundaryPoints] = useState<[number, number][]>([])

  // Map visualization data (pushed up from panels)
  const [mapViz, setMapViz] = useState<MapVisualization>({})

  // Overlay settings (lifted for real-time preview on map)
  const [overlayOpacity, setOverlayOpacity] = useState(mapConfig?.map_overlay_opacity ?? 0.9)
  const [overlayRotation, setOverlayRotation] = useState(mapConfig?.map_overlay_rotation ?? 0)
  const [overlayScale, setOverlayScale] = useState(mapConfig?.map_overlay_scale ?? 1.0)
  const [overlayOffsetX, setOverlayOffsetX] = useState(mapConfig?.map_overlay_offset_x ?? 0)
  const [overlayOffsetY, setOverlayOffsetY] = useState(mapConfig?.map_overlay_offset_y ?? 0)
  const updateMapConfig = useUpdateMapConfig()
  const overlayDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Keep a ref to the latest values so the debounced save always gets current state
  const overlaySettingsRef = useRef({ opacity: overlayOpacity, rotation: overlayRotation, scale: overlayScale, offsetX: overlayOffsetX, offsetY: overlayOffsetY })
  overlaySettingsRef.current = { opacity: overlayOpacity, rotation: overlayRotation, scale: overlayScale, offsetX: overlayOffsetX, offsetY: overlayOffsetY }

  // Sync from server when mapConfig changes
  useEffect(() => {
    if (mapConfig) {
      setOverlayOpacity(mapConfig.map_overlay_opacity ?? 0.9)
      setOverlayRotation(mapConfig.map_overlay_rotation ?? 0)
      setOverlayScale(mapConfig.map_overlay_scale ?? 1.0)
      setOverlayOffsetX(mapConfig.map_overlay_offset_x ?? 0)
      setOverlayOffsetY(mapConfig.map_overlay_offset_y ?? 0)
    }
  }, [mapConfig])

  // Auto-save overlay settings with debounce
  const saveOverlaySettings = useCallback(() => {
    if (overlayDebounceRef.current) clearTimeout(overlayDebounceRef.current)
    overlayDebounceRef.current = setTimeout(() => {
      const s = overlaySettingsRef.current
      updateMapConfig.mutate({
        eventId: eventIdNum,
        data: {
          map_overlay_opacity: s.opacity,
          map_overlay_rotation: s.rotation,
          map_overlay_scale: s.scale,
          map_overlay_offset_x: s.offsetX,
          map_overlay_offset_y: s.offsetY,
        } as Partial<MapConfig>,
      })
    }, 500)
  }, [eventIdNum, updateMapConfig])

  const handleOverlayOpacityChange = useCallback((v: number) => {
    setOverlayOpacity(v)
    saveOverlaySettings()
  }, [saveOverlaySettings])

  const handleOverlayRotationChange = useCallback((v: number) => {
    setOverlayRotation(v)
    saveOverlaySettings()
  }, [saveOverlaySettings])

  const handleOverlayScaleChange = useCallback((v: number) => {
    setOverlayScale(v)
    saveOverlaySettings()
  }, [saveOverlaySettings])

  const handleOverlayOffsetXChange = useCallback((v: number) => {
    setOverlayOffsetX(v)
    saveOverlaySettings()
  }, [saveOverlaySettings])

  const handleOverlayOffsetYChange = useCallback((v: number) => {
    setOverlayOffsetY(v)
    saveOverlaySettings()
  }, [saveOverlaySettings])

  // ESC to cancel placement or boundary drawing
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (selectedVendorId) setSelectedVendorId(null)
        if (drawingBoundary) {
          setDrawingBoundary(false)
          setBoundaryPoints([])
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedVendorId, drawingBoundary])

  if (!canViewSiteMap) {
    return <AccessDenied message="You don't have permission to view the site map." />
  }

  const vendorEvents = vendorEventsData?.results || []

  return (
    <div className="flex h-[calc(100vh-4rem)] overflow-hidden">
      {/* Sidebar */}
      <div
        className={`${sidebarOpen ? 'w-80' : 'w-0'} transition-all duration-200 shrink-0 border-r border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden`}
      >
        <div className="w-80 h-full flex flex-col">
          {/* Mode tabs */}
          <div className="flex border-b border-neutral-200 dark:border-neutral-700">
            {[
              { id: 'setup' as ViewMode, label: 'Setup', icon: Settings },
              { id: 'heatmap' as ViewMode, label: 'Heatmap', icon: Flame },
              { id: 'timeline' as ViewMode, label: 'Timeline', icon: Play },
              { id: 'journey' as ViewMode, label: 'Journey', icon: RouteIcon },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setMode(tab.id); setMapViz({}); window.history.replaceState(null, '', `#${tab.id}`) }}
                className={`flex-1 flex flex-col items-center gap-1 py-3 px-1 text-xs font-medium transition-colors ${
                  mode === tab.id
                    ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                    : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Panel content */}
          <div className="flex-1 overflow-y-auto p-4">
            {mode === 'setup' && (
              <SetupPanel
                eventId={eventIdNum}
                mapConfig={mapConfig || null}
                vendorEvents={vendorEvents}
                canEdit={canEditEvents}
                selectedVendorId={selectedVendorId}
                onSelectVendor={setSelectedVendorId}
                drawingBoundary={drawingBoundary}
                onDrawBoundaryToggle={(active) => {
                  setDrawingBoundary(active)
                  if (!active) setBoundaryPoints([])
                }}
                boundaryPoints={boundaryPoints}
                onUndoBoundaryPoint={() => setBoundaryPoints((p) => p.slice(0, -1))}
                overlayOpacity={overlayOpacity}
                overlayRotation={overlayRotation}
                overlayScale={overlayScale}
                overlayOffsetX={overlayOffsetX}
                overlayOffsetY={overlayOffsetY}
                onOverlayOpacityChange={handleOverlayOpacityChange}
                onOverlayRotationChange={handleOverlayRotationChange}
                onOverlayScaleChange={handleOverlayScaleChange}
                onOverlayOffsetXChange={handleOverlayOffsetXChange}
                onOverlayOffsetYChange={handleOverlayOffsetYChange}
              />
            )}
            {mode === 'heatmap' && (
              <HeatmapPanel eventId={eventIdNum} onDataChange={(locs) => setMapViz({ heatmapLocations: locs })} />
            )}
            {mode === 'timeline' && (
              <TimelinePanel eventId={eventIdNum} onFrameChange={(frame) => setMapViz({ timelineFrame: frame })} />
            )}
            {mode === 'journey' && (
              <JourneyPanel eventId={eventIdNum} onDataChange={(journeys, playbackStep) => setMapViz((prev) => ({ ...prev, journeys, journeyPlaybackStep: playbackStep }))} />
            )}
          </div>
        </div>
      </div>

      {/* Map area */}
      <div className="flex-1 relative">
        {/* Toggle sidebar */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-r-lg p-1.5 shadow-md hover:bg-neutral-50 dark:hover:bg-neutral-750 transition-colors"
        >
          {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        {/* Placement mode banner */}
        {selectedVendorId && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-full shadow-lg text-sm font-medium">
            <Crosshair className="w-4 h-4" />
            Click on the map to place vendor
            <button
              onClick={() => setSelectedVendorId(null)}
              className="ml-1 p-0.5 rounded-full hover:bg-blue-700 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Boundary drawing banner */}
        {drawingBoundary && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-full shadow-lg text-sm font-medium">
            <Pen className="w-4 h-4" />
            Click to add points ({boundaryPoints.length} pts) &mdash; click first point to close
            <button
              onClick={() => { setDrawingBoundary(false); setBoundaryPoints([]) }}
              className="ml-1 p-0.5 rounded-full hover:bg-purple-700 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {configLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-neutral-400" />
          </div>
        ) : (
          <MapCanvas
            eventId={eventIdNum}
            mapConfig={mapConfig || null}
            vendorEvents={vendorEvents}
            mode={mode}
            mapViz={mapViz}
            selectedVendorId={selectedVendorId}
            onVendorPlaced={() => setSelectedVendorId(null)}
            drawingBoundary={drawingBoundary}
            boundaryPoints={boundaryPoints}
            onAddBoundaryPoint={(pt) => setBoundaryPoints((prev) => [...prev, pt])}
            onCloseBoundary={() => setDrawingBoundary(false)}
            venueAddress={[event?.address, event?.city, event?.zip_code, event?.country].filter(Boolean).join(', ') || undefined}
            isDark={isDark}
            overlayOpacity={overlayOpacity}
            overlayRotation={overlayRotation}
            overlayScale={overlayScale}
            overlayOffsetX={overlayOffsetX}
            overlayOffsetY={overlayOffsetY}
          />
        )}
      </div>
    </div>
  )
}

// ────────────────────── Setup Panel ──────────────────────
function SetupPanel({
  eventId,
  mapConfig,
  vendorEvents,
  canEdit,
  selectedVendorId,
  onSelectVendor,
  drawingBoundary,
  onDrawBoundaryToggle,
  boundaryPoints,
  onUndoBoundaryPoint,
  overlayOpacity,
  overlayRotation,
  overlayScale,
  overlayOffsetX,
  overlayOffsetY,
  onOverlayOpacityChange,
  onOverlayRotationChange,
  onOverlayScaleChange,
  onOverlayOffsetXChange,
  onOverlayOffsetYChange,
}: {
  eventId: number
  mapConfig: MapConfig | null
  vendorEvents: VendorEvent[]
  canEdit: boolean
  selectedVendorId: number | null
  onSelectVendor: (id: number | null) => void
  drawingBoundary: boolean
  onDrawBoundaryToggle: (active: boolean) => void
  boundaryPoints: [number, number][]
  onUndoBoundaryPoint: () => void
  overlayOpacity: number
  overlayRotation: number
  overlayScale: number
  overlayOffsetX: number
  overlayOffsetY: number
  onOverlayOpacityChange: (v: number) => void
  onOverlayRotationChange: (v: number) => void
  onOverlayScaleChange: (v: number) => void
  onOverlayOffsetXChange: (v: number) => void
  onOverlayOffsetYChange: (v: number) => void
}) {
  const updateMapConfig = useUpdateMapConfig()
  const { token } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [overlaySettingsOpen, setOverlaySettingsOpen] = useState(false)
  const [vendorTab, setVendorTab] = useState<'unplaced' | 'placed'>('placed')
  const queryClient = useQueryClient()

  const handleRemoveVendor = async (vendorEventId: number) => {
    if (!token) return
    try {
      await apiClient.updateVendorEvent(token, vendorEventId, { x: null, y: null } as any)
      queryClient.invalidateQueries({ queryKey: queryKeys.mapConfig(eventId) })
    } catch (err) {
      console.error('Failed to remove vendor placement:', err)
    }
  }

  const handleImageUpload = async (file: File) => {
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('map_overlay_image', file)
      await updateMapConfig.mutateAsync({ eventId, data: formData })
    } catch (e) {
      console.error('Failed to upload map image:', e)
    } finally {
      setUploading(false)
    }
  }

  const handleRemoveImage = async () => {
    await updateMapConfig.mutateAsync({
      eventId,
      data: { map_overlay_image: null } as unknown as FormData,
    })
  }

  // Use mapConfig.vendor_locations as source of truth for placed vendors (filter out null x/y)
  const validLocations = (mapConfig?.vendor_locations || []).filter((vl) => vl.x != null && vl.y != null)
  const placedVendorIds = new Set(validLocations.map((vl) => vl.vendor_event_id))
  const placedVendors = vendorEvents
    .filter((ve) => placedVendorIds.has(ve.id))
    .sort((a, b) => {
      const nameA = (a as any).vendor_name || (a as any).name || a.booth || ''
      const nameB = (b as any).vendor_name || (b as any).name || b.booth || ''
      return nameA.localeCompare(nameB)
    })
  const unplacedVendors = vendorEvents
    .filter((ve) => !placedVendorIds.has(ve.id))
    .sort((a, b) => {
      const nameA = (a as any).vendor_name || (a as any).name || a.booth || ''
      const nameB = (b as any).vendor_name || (b as any).name || b.booth || ''
      return nameA.localeCompare(nameB)
    })

  // Build vendor number map (1-indexed, consistent between sidebar and map)
  const vendorNumberMap = new Map<number, number>()
  placedVendors.forEach((ve, i) => vendorNumberMap.set(ve.id, i + 1))

  return (
    <div className="space-y-6">
      {/* Map Image */}
      <div>
        <button
          type="button"
          onClick={() => mapConfig?.map_overlay_image && setOverlaySettingsOpen(!overlaySettingsOpen)}
          className="flex items-center justify-between w-full mb-2"
        >
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Map Image</h3>
          {mapConfig?.map_overlay_image && (
            <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${overlaySettingsOpen ? 'rotate-180' : ''}`} />
          )}
        </button>
        {mapConfig?.map_overlay_image ? (
          <div className="space-y-2">
            {overlaySettingsOpen && (
              <div className="space-y-3">
                <div className="relative rounded-lg overflow-hidden border border-neutral-200 dark:border-neutral-700">
                  <img
                    src={mapConfig.map_overlay_image}
                    alt="Site map"
                    className="w-full h-40 object-cover"
                  />
                </div>
                {canEdit && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                    >
                      <Upload className="w-4 h-4" />
                      Replace
                    </button>
                    <button
                      onClick={handleRemoveImage}
                      className="flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <div>
                  <label className="text-xs text-neutral-500 dark:text-neutral-400">
                    Opacity: {Math.round(overlayOpacity * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={overlayOpacity}
                    onChange={(e) => onOverlayOpacityChange(parseFloat(e.target.value))}
                    className="w-full h-2 bg-neutral-200 dark:bg-neutral-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
                <div>
                  <label className="text-xs text-neutral-500 dark:text-neutral-400">
                    Rotation: {Math.round(overlayRotation)}°
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="360"
                    step="1"
                    value={overlayRotation}
                    onChange={(e) => onOverlayRotationChange(parseFloat(e.target.value))}
                    className="w-full h-2 bg-neutral-200 dark:bg-neutral-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
                <div>
                  <label className="text-xs text-neutral-500 dark:text-neutral-400">
                    Scale: {Math.round(overlayScale * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0.5"
                    max="2"
                    step="0.05"
                    value={overlayScale}
                    onChange={(e) => onOverlayScaleChange(parseFloat(e.target.value))}
                    className="w-full h-2 bg-neutral-200 dark:bg-neutral-700 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-neutral-500 dark:text-neutral-400">
                      Offset X: {overlayOffsetX}%
                    </label>
                    <input
                      type="range"
                      min="-50"
                      max="50"
                      step="0.5"
                      value={overlayOffsetX}
                      onChange={(e) => onOverlayOffsetXChange(parseFloat(e.target.value))}
                      className="w-full h-2 bg-neutral-200 dark:bg-neutral-700 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-neutral-500 dark:text-neutral-400">
                      Offset Y: {overlayOffsetY}%
                    </label>
                    <input
                      type="range"
                      min="-50"
                      max="50"
                      step="0.5"
                      value={overlayOffsetY}
                      onChange={(e) => onOverlayOffsetYChange(parseFloat(e.target.value))}
                      className="w-full h-2 bg-neutral-200 dark:bg-neutral-700 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                </div>
                {!mapConfig?.map_boundary && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    Draw a site boundary to position the overlay image on the map.
                  </p>
                )}
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => canEdit && fileInputRef.current?.click()}
            disabled={!canEdit || uploading}
            className="w-full flex flex-col items-center justify-center py-8 border-2 border-dashed border-neutral-300 dark:border-neutral-600 rounded-lg hover:border-blue-400 dark:hover:border-blue-500 transition-colors cursor-pointer"
          >
            {uploading ? (
              <Loader2 className="w-8 h-8 animate-spin text-neutral-400 mb-2" />
            ) : (
              <Upload className="w-8 h-8 text-neutral-400 mb-2" />
            )}
            <span className="text-sm text-neutral-500 dark:text-neutral-400">
              {uploading ? 'Uploading...' : 'Upload site map image'}
            </span>
            <span className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
              PNG, JPG, or SVG
            </span>
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) handleImageUpload(file)
            e.target.value = ''
          }}
        />
      </div>

      {/* Site Boundary */}
      <div>
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
          Site Boundary
        </h3>
        {drawingBoundary ? (
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
              <p className="text-xs text-purple-700 dark:text-purple-300 mb-1">
                Click on the map to add points. Click the first point to close the shape.
              </p>
              <p className="text-xs text-purple-600 dark:text-purple-400 tabular-nums">
                {boundaryPoints.length} point{boundaryPoints.length !== 1 ? 's' : ''} added
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={onUndoBoundaryPoint}
                disabled={boundaryPoints.length === 0}
                className="flex items-center justify-center gap-1 px-3 py-2 text-sm text-neutral-600 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-lg transition-colors disabled:opacity-50"
              >
                <Undo2 className="w-3.5 h-3.5" />
                Undo
              </button>
              <button
                onClick={() => onDrawBoundaryToggle(false)}
                className="flex-1 px-4 py-2 text-sm font-medium text-neutral-600 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <button
              onClick={() => onDrawBoundaryToggle(true)}
              disabled={!canEdit}
              className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <Pen className="w-4 h-4" />
              {mapConfig?.map_boundary ? 'Redraw Boundary' : 'Draw Site Boundary'}
            </button>
            {mapConfig?.map_boundary && (
              <p className="text-xs text-neutral-500">
                Boundary set ({(mapConfig.map_boundary as any).coordinates?.[0]?.length - 1 || 0} points)
              </p>
            )}
          </div>
        )}
      </div>

      {/* Vendor Placement */}
      <div>
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
          Vendor Placement
          <span className="ml-2 text-xs font-normal text-neutral-500">
            {placedVendors.length}/{vendorEvents.length} placed
          </span>
        </h3>
        {!mapConfig?.map_boundary ? (
          <p className="text-xs text-neutral-400 dark:text-neutral-500 py-2">
            Draw a site boundary first before placing vendors.
          </p>
        ) : (
          <>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">
              Click a vendor, then click on the map to place it.
            </p>

            {/* Tabs */}
            <div className="flex rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden mb-2">
              <button
                onClick={() => setVendorTab('placed')}
                className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${
                  vendorTab === 'placed'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-750'
                }`}
              >
                Placed ({placedVendors.length})
              </button>
              <button
                onClick={() => setVendorTab('unplaced')}
                className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${
                  vendorTab === 'unplaced'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-750'
                }`}
              >
                Unplaced ({unplacedVendors.length})
              </button>
            </div>

            {/* Tab content */}
            <div className="space-y-1 max-h-[calc(100vh-28rem)] overflow-y-auto">
              {vendorTab === 'unplaced' ? (
                unplacedVendors.length > 0 ? (
                  unplacedVendors.map((ve) => (
                    <VendorPlaceItem
                      key={ve.id}
                      vendorEvent={ve}
                      isSelected={selectedVendorId === ve.id}
                      onSelect={() => onSelectVendor(selectedVendorId === ve.id ? null : ve.id)}
                    />
                  ))
                ) : (
                  <p className="text-xs text-neutral-400 text-center py-4">All vendors placed</p>
                )
              ) : (
                placedVendors.length > 0 ? (
                  placedVendors.map((ve) => (
                    <VendorPlaceItem
                      key={ve.id}
                      vendorEvent={ve}
                      placed
                      number={vendorNumberMap.get(ve.id)}
                      isSelected={selectedVendorId === ve.id}
                      onSelect={() => onSelectVendor(selectedVendorId === ve.id ? null : ve.id)}
                      onRemove={() => handleRemoveVendor(ve.id)}
                    />
                  ))
                ) : (
                  <p className="text-xs text-neutral-400 text-center py-4">No vendors placed yet</p>
                )
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function VendorPlaceItem({
  vendorEvent,
  placed,
  number,
  isSelected,
  onSelect,
  onRemove,
}: {
  vendorEvent: VendorEvent
  placed?: boolean
  number?: number
  isSelected: boolean
  onSelect: () => void
  onRemove?: () => void
}) {
  const name = (vendorEvent as any).vendor_name || (vendorEvent as any).name || vendorEvent.booth || `Location ${vendorEvent.id}`

  return (
    <div
      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors text-left ${
        isSelected
          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 ring-2 ring-blue-500'
          : placed
            ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300'
            : 'bg-neutral-50 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-750'
      }`}
    >
      <button onClick={onSelect} className="flex items-center gap-2 flex-1 min-w-0">
        {placed && number ? (
          <span className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
            {number}
          </span>
        ) : isSelected ? (
          <Crosshair className="w-3.5 h-3.5 shrink-0 text-blue-600" />
        ) : (
          <MapPin className="w-3.5 h-3.5 shrink-0" />
        )}
        <span className="truncate flex-1">{name}</span>
      </button>
      {placed && !isSelected && onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove() }}
          className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 shrink-0 px-1"
          title="Remove from map"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
      {isSelected && (
        <span className="text-xs text-blue-600 dark:text-blue-400 shrink-0">Placing...</span>
      )}
    </div>
  )
}

// ────────────────────── Heatmap Panel ──────────────────────
function HeatmapPanel({ eventId, onDataChange }: { eventId: number; onDataChange: (locations: HeatmapLocation[]) => void }) {
  const { data: event } = useEvent(eventId)

  // Auto-populate from event start/end times
  const defaultStart = instantToEventLocal(event?.start_date_time, event?.timezone || DEFAULT_EVENT_TIMEZONE)
  const defaultEnd = instantToEventLocal(event?.end_date_time, event?.timezone || DEFAULT_EVENT_TIMEZONE)

  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [metric, setMetric] = useState<'transactions' | 'revenue'>('transactions')

  // Set defaults once event loads
  useEffect(() => {
    if (defaultStart && !startDate) setStartDate(defaultStart)
    if (defaultEnd && !endDate) setEndDate(defaultEnd)
  }, [defaultStart, defaultEnd])

  const { data: heatmapData, isLoading } = useMapHeatmap(
    eventId,
    { start_date: startDate ? eventLocalToServerNaive(startDate, event?.timezone || DEFAULT_EVENT_TIMEZONE, "yyyy-MM-dd'T'HH:mm") : undefined, end_date: endDate ? eventLocalToServerNaive(endDate, event?.timezone || DEFAULT_EVENT_TIMEZONE, "yyyy-MM-dd'T'HH:mm") : undefined, metric },
    { enabled: true }
  )

  // Push heatmap data to map
  useEffect(() => {
    if (heatmapData?.locations) {
      onDataChange(heatmapData.locations)
    }
  }, [heatmapData?.locations])

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Heatmap</h3>

      {/* Date/time range */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-neutral-500 dark:text-neutral-400">Start ({timeZoneLabel(event?.timezone || DEFAULT_EVENT_TIMEZONE)})</label>
          <input
            type="datetime-local"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full mt-1 px-2 py-1.5 text-xs border border-neutral-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
          />
        </div>
        <div>
          <label className="text-xs text-neutral-500 dark:text-neutral-400">End ({timeZoneLabel(event?.timezone || DEFAULT_EVENT_TIMEZONE)})</label>
          <input
            type="datetime-local"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full mt-1 px-2 py-1.5 text-xs border border-neutral-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
          />
        </div>
      </div>

      {/* Metric toggle */}
      <div>
        <label className="text-xs text-neutral-500 dark:text-neutral-400 mb-1 block">Metric</label>
        <div className="flex rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          {(['transactions', 'revenue'] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMetric(m)}
              className={`flex-1 px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                metric === m
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-750'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div>
        <label className="text-xs text-neutral-500 dark:text-neutral-400 mb-2 block">Intensity</label>
        <div className="flex items-center gap-1">
          <span className="text-xs text-neutral-400">Low</span>
          <div className="flex-1 h-3 rounded-full bg-gradient-to-r from-blue-400 via-yellow-400 to-red-500" />
          <span className="text-xs text-neutral-400">High</span>
        </div>
      </div>

      {/* Location summary */}
      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-neutral-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading heatmap data...
        </div>
      )}
      {heatmapData && (
        <div>
          <h4 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase mb-2">
            Locations ({heatmapData.locations.length})
          </h4>
          <div className="space-y-1 overflow-y-auto">
            {heatmapData.locations
              .sort((a, b) => b.intensity - a.intensity)
              .map((loc) => (
                <div
                  key={loc.vendor_event_id}
                  className="flex items-center justify-between px-2 py-1.5 rounded-md bg-neutral-50 dark:bg-neutral-800 text-sm"
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: intensityToColor(loc.intensity) }}
                    />
                    <span className="text-neutral-700 dark:text-neutral-300 truncate max-w-[140px]">
                      {loc.vendor_name}
                    </span>
                  </div>
                  <span className="text-xs text-neutral-500 tabular-nums">
                    {metric === 'revenue'
                      ? `$${(loc.revenue ?? 0).toLocaleString()}`
                      : `${loc.transaction_count ?? 0} txn`}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ────────────────────── Timeline Panel ──────────────────────
function TimelinePanel({ eventId, onFrameChange }: { eventId: number; onFrameChange: (frame: TimelineInterval['locations'] | undefined) => void }) {
  const { data: event } = useEvent(eventId)

  const defaultStart = instantToEventLocal(event?.start_date_time, event?.timezone || DEFAULT_EVENT_TIMEZONE)
  const defaultEnd = instantToEventLocal(event?.end_date_time, event?.timezone || DEFAULT_EVENT_TIMEZONE)

  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [interval_, setInterval_] = useState<'minute' | 'hour'>('hour')
  const [metric, setMetric] = useState<'transactions' | 'revenue'>('transactions')
  const [playing, setPlaying] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [speed, setSpeed] = useState(1)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Set defaults once event loads
  useEffect(() => {
    if (defaultStart && !startDate) setStartDate(defaultStart)
    if (defaultEnd && !endDate) setEndDate(defaultEnd)
  }, [defaultStart, defaultEnd])

  const { data: timelineData, isLoading } = useMapTimeline(
    eventId,
    { start_date: startDate ? eventLocalToServerNaive(startDate, event?.timezone || DEFAULT_EVENT_TIMEZONE, "yyyy-MM-dd'T'HH:mm") : undefined, end_date: endDate ? eventLocalToServerNaive(endDate, event?.timezone || DEFAULT_EVENT_TIMEZONE, "yyyy-MM-dd'T'HH:mm") : undefined, interval: interval_, metric },
    { enabled: true }
  )

  const intervals = (timelineData as TimelineResponse)?.intervals || []
  const summary = (timelineData as TimelineResponse)?.summary

  // Push current frame to map
  useEffect(() => {
    const frame = intervals[currentIndex]?.locations
    onFrameChange(frame)
  }, [currentIndex, intervals])

  // Playback control
  useEffect(() => {
    if (playing && intervals.length > 0) {
      timerRef.current = setInterval(() => {
        setCurrentIndex((prev) => {
          if (prev >= intervals.length - 1) {
            setPlaying(false)
            return prev
          }
          return prev + 1
        })
      }, 1000 / speed)
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [playing, speed, intervals.length])

  const togglePlay = () => {
    if (currentIndex >= intervals.length - 1) {
      setCurrentIndex(0)
    }
    setPlaying(!playing)
  }

  const currentTimestamp = intervals[currentIndex]?.timestamp
    ? new Date(intervals[currentIndex].timestamp).toLocaleString()
    : '--'

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Timeline Playback</h3>

      {/* Date/time range */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-neutral-500 dark:text-neutral-400">Start ({timeZoneLabel(event?.timezone || DEFAULT_EVENT_TIMEZONE)})</label>
          <input
            type="datetime-local"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full mt-1 px-2 py-1.5 text-xs border border-neutral-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
          />
        </div>
        <div>
          <label className="text-xs text-neutral-500 dark:text-neutral-400">End ({timeZoneLabel(event?.timezone || DEFAULT_EVENT_TIMEZONE)})</label>
          <input
            type="datetime-local"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full mt-1 px-2 py-1.5 text-xs border border-neutral-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
          />
        </div>
      </div>

      {/* Controls row */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-neutral-500 dark:text-neutral-400 mb-1 block">Interval</label>
          <div className="flex rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
            {(['hour', 'minute'] as const).map((i) => (
              <button
                key={i}
                onClick={() => setInterval_(i)}
                className={`flex-1 px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                  interval_ === i
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
                }`}
              >
                {i}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs text-neutral-500 dark:text-neutral-400 mb-1 block">Speed</label>
          <div className="flex rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
            {[1, 2, 4].map((s) => (
              <button
                key={s}
                onClick={() => setSpeed(s)}
                className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${
                  speed === s
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
                }`}
              >
                {s}x
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Metric toggle */}
      <div>
        <label className="text-xs text-neutral-500 dark:text-neutral-400 mb-1 block">Metric</label>
        <div className="flex rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          {(['transactions', 'revenue'] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMetric(m)}
              className={`flex-1 px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                metric === m
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {/* Playback bar */}
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-neutral-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading timeline...
        </div>
      ) : intervals.length > 0 ? (
        <div className="space-y-2">
          <div className="text-center text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">
            {currentTimestamp}
          </div>
          <input
            type="range"
            min="0"
            max={Math.max(0, intervals.length - 1)}
            value={currentIndex}
            onChange={(e) => {
              setCurrentIndex(parseInt(e.target.value))
              setPlaying(false)
            }}
            className="w-full h-2 bg-neutral-200 dark:bg-neutral-700 rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => { setCurrentIndex(0); setPlaying(false) }}
              className="p-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
            >
              <SkipBack className="w-4 h-4" />
            </button>
            <button
              onClick={togglePlay}
              className="p-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
            <button
              onClick={() => { setCurrentIndex(intervals.length - 1); setPlaying(false) }}
              className="p-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
            >
              <SkipForward className="w-4 h-4" />
            </button>
          </div>
          <div className="text-xs text-neutral-400 text-center">
            Frame {currentIndex + 1} / {intervals.length}
          </div>
        </div>
      ) : (
        <p className="text-xs text-neutral-400 text-center py-2">
          Select a date range to load timeline data.
        </p>
      )}

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 gap-2">
          <div className="p-2 rounded-md bg-neutral-50 dark:bg-neutral-800">
            <div className="text-xs text-neutral-500">Transactions</div>
            <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              {(summary.total_transactions ?? 0).toLocaleString()}
            </div>
          </div>
          <div className="p-2 rounded-md bg-neutral-50 dark:bg-neutral-800">
            <div className="text-xs text-neutral-500">Customers</div>
            <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              {(summary.unique_customers ?? 0).toLocaleString()}
            </div>
          </div>
          <div className="p-2 rounded-md bg-neutral-50 dark:bg-neutral-800 col-span-2">
            <div className="text-xs text-neutral-500">Busiest Location</div>
            <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              {summary.busiest_location?.name || '--'}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ────────────────────── Journey Panel ──────────────────────
function JourneyPanel({ eventId, onDataChange }: { eventId: number; onDataChange: (journeys: MapVisualization['journeys'], playbackStep?: number) => void }) {
  const { data: event } = useEvent(eventId)

  const defaultStart = instantToEventLocal(event?.start_date_time, event?.timezone || DEFAULT_EVENT_TIMEZONE)
  const defaultEnd = instantToEventLocal(event?.end_date_time, event?.timezone || DEFAULT_EVENT_TIMEZONE)

  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [trackedUsers, setTrackedUsers] = useState<{ id: number; name: string }[]>([])
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const searchRef = useRef<HTMLDivElement>(null)

  // Playback state
  const [playing, setPlaying] = useState(false)
  const [currentStep, setCurrentStep] = useState(-1) // -1 = show all
  const [speed, setSpeed] = useState(1)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Set defaults once event loads
  useEffect(() => {
    if (defaultStart && !startDate) setStartDate(defaultStart)
    if (defaultEnd && !endDate) setEndDate(defaultEnd)
  }, [defaultStart, defaultEnd])

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const { data: searchResults, isLoading: searchLoading } = useEventUsers(
    eventId,
    { search: debouncedSearch, page_size: 10, type: 'attendees' },
    { enabled: debouncedSearch.length >= 2 }
  )

  const userIds = trackedUsers.map((u) => u.id)

  const { data: journeyData, isLoading } = useMapTimeline(
    eventId,
    {
      start_date: startDate ? eventLocalToServerNaive(startDate, event?.timezone || DEFAULT_EVENT_TIMEZONE, "yyyy-MM-dd'T'HH:mm") : undefined,
      end_date: endDate ? eventLocalToServerNaive(endDate, event?.timezone || DEFAULT_EVENT_TIMEZONE, "yyyy-MM-dd'T'HH:mm") : undefined,
      user_ids: userIds.length > 0 ? userIds.join(',') : undefined,
    },
    { enabled: userIds.length > 0 }
  )

  const journeys = (journeyData as JourneyResponse)?.journeys || []

  // Maximum steps across all journeys (for playback)
  const maxSteps = useMemo(() => {
    if (journeys.length === 0) return 0
    return Math.max(...journeys.map((j) => j.path.length))
  }, [journeys])

  const addUser = (user: { id: number; first_name: string; last_name: string }) => {
    if (!trackedUsers.some((u) => u.id === user.id)) {
      setTrackedUsers([...trackedUsers, { id: user.id, name: `${user.first_name} ${user.last_name}`.trim() || `User ${user.id}` }])
    }
    setSearchTerm('')
    setDebouncedSearch('')
    setShowDropdown(false)
  }

  const removeUser = (id: number) => {
    setTrackedUsers(trackedUsers.filter((u) => u.id !== id))
  }

  // Color palette for journey paths
  const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899']

  // Build a name map from tracked users for display
  const userNameMap = useMemo(() => {
    const map: Record<number, string> = {}
    trackedUsers.forEach((u) => { map[u.id] = u.name })
    return map
  }, [trackedUsers])

  // Push journey data + playback step to map
  const journeysForMap = useMemo(() => {
    if (journeys.length === 0) return undefined
    return journeys.map((j, i) => ({
      user_id: j.user_id,
      path: j.path,
      color: colors[i % colors.length],
    }))
  }, [journeys])

  useEffect(() => {
    onDataChange(journeysForMap, currentStep >= 0 ? currentStep : undefined)
  }, [journeysForMap, currentStep])

  // Playback timer
  useEffect(() => {
    if (playing && maxSteps > 0) {
      timerRef.current = setInterval(() => {
        setCurrentStep((prev) => {
          const next = prev + 1
          if (next >= maxSteps - 1) {
            setPlaying(false)
            return maxSteps - 1
          }
          return next
        })
      }, 1000 / speed)
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [playing, maxSteps, speed])

  const handlePlayPause = () => {
    if (!playing) {
      // If at end or showing all, start from beginning
      if (currentStep < 0 || currentStep >= maxSteps - 1) {
        setCurrentStep(0)
      }
      setPlaying(true)
    } else {
      setPlaying(false)
    }
  }

  const handleReset = () => {
    setPlaying(false)
    setCurrentStep(-1) // -1 = show all
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Customer Journey</h3>

      {/* Customer search */}
      <div ref={searchRef} className="relative">
        <label className="text-xs text-neutral-500 dark:text-neutral-400 mb-1 block">Search Customer</label>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setShowDropdown(true) }}
            onFocus={() => { if (searchTerm.length >= 2) setShowDropdown(true) }}
            placeholder="Search by name, email, or phone..."
            className="w-full pl-7 pr-2 py-1.5 text-sm border border-neutral-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
          />
        </div>

        {/* Search dropdown */}
        {showDropdown && debouncedSearch.length >= 2 && (
          <div className="absolute z-50 top-full mt-1 w-full bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-md shadow-lg max-h-48 overflow-y-auto">
            {searchLoading && (
              <div className="flex items-center gap-2 px-3 py-2 text-xs text-neutral-500">
                <Loader2 className="w-3 h-3 animate-spin" />
                Searching...
              </div>
            )}
            {!searchLoading && searchResults?.results && searchResults.results.length === 0 && (
              <div className="px-3 py-2 text-xs text-neutral-500">No customers found</div>
            )}
            {!searchLoading && searchResults?.results?.map((user) => {
              const alreadyAdded = trackedUsers.some((u) => u.id === user.id)
              return (
                <button
                  key={user.id}
                  onClick={() => addUser(user)}
                  disabled={alreadyAdded}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors ${alreadyAdded ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                  <div className="font-medium text-neutral-900 dark:text-neutral-100">
                    {user.first_name} {user.last_name}
                  </div>
                  <div className="text-xs text-neutral-500">{user.email}</div>
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Tracked users */}
      {trackedUsers.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {trackedUsers.map((user, i) => (
            <span
              key={user.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white"
              style={{ backgroundColor: colors[i % colors.length] }}
            >
              {user.name}
              <button onClick={() => removeUser(user.id)} className="hover:opacity-75">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Date/time range */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-neutral-500 dark:text-neutral-400">Start ({timeZoneLabel(event?.timezone || DEFAULT_EVENT_TIMEZONE)})</label>
          <input
            type="datetime-local"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full mt-1 px-2 py-1.5 text-xs border border-neutral-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
          />
        </div>
        <div>
          <label className="text-xs text-neutral-500 dark:text-neutral-400">End ({timeZoneLabel(event?.timezone || DEFAULT_EVENT_TIMEZONE)})</label>
          <input
            type="datetime-local"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full mt-1 px-2 py-1.5 text-xs border border-neutral-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
          />
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-neutral-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading journeys...
        </div>
      )}

      {/* Journey list */}
      {journeys.length > 0 && (
        <div className="space-y-2">
          {journeys.map((journey, i) => (
            <div
              key={journey.user_id}
              className="p-2 rounded-md bg-neutral-50 dark:bg-neutral-800 border-l-4"
              style={{ borderColor: colors[i % colors.length] }}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  {userNameMap[journey.user_id] || `User ${journey.user_id}`}
                </span>
                <span className="text-xs text-neutral-500">
                  ${journey.total_spend.toFixed(2)}
                </span>
              </div>
              <div className="text-xs text-neutral-500">
                {journey.path.length} stops
              </div>
              {/* Current step info during playback */}
              {currentStep >= 0 && currentStep < journey.path.length && (
                <div className="mt-1 text-xs text-neutral-600 dark:text-neutral-400 flex items-center gap-1">
                  <span className="font-medium" style={{ color: colors[i % colors.length] }}>
                    Step {currentStep + 1}
                  </span>
                  <span>&middot;</span>
                  <span>${journey.path[currentStep].amount.toFixed(2)}</span>
                  <span>&middot;</span>
                  <span>{new Date(journey.path[currentStep].timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              )}
            </div>
          ))}

          {/* Playback controls */}
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={handleReset}
              className="p-1.5 rounded-md text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
              title="Reset (show all)"
            >
              <SkipBack className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={handlePlayPause}
              className="p-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-colors"
              title={playing ? 'Pause' : 'Play sequence'}
            >
              {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            </button>
            <button
              onClick={() => setCurrentStep((prev) => Math.min(prev + 1, maxSteps - 1))}
              disabled={currentStep >= maxSteps - 1}
              className="p-1.5 rounded-md text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors disabled:opacity-30"
              title="Next step"
            >
              <SkipForward className="w-3.5 h-3.5" />
            </button>

            {/* Step indicator */}
            <span className="text-xs text-neutral-500 ml-auto">
              {currentStep >= 0 ? `${currentStep + 1} / ${maxSteps}` : `${maxSteps} stops`}
            </span>

            {/* Speed selector */}
            <select
              value={speed}
              onChange={(e) => setSpeed(Number(e.target.value))}
              className="text-xs border border-neutral-200 dark:border-neutral-700 rounded px-1 py-0.5 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
            >
              <option value={0.5}>0.5x</option>
              <option value={1}>1x</option>
              <option value={2}>2x</option>
              <option value={4}>4x</option>
            </select>
          </div>

          {/* Scrubber */}
          {maxSteps > 1 && (
            <input
              type="range"
              min={0}
              max={maxSteps - 1}
              value={currentStep >= 0 ? currentStep : maxSteps - 1}
              onChange={(e) => { setPlaying(false); setCurrentStep(Number(e.target.value)) }}
              className="w-full h-1.5 accent-blue-600"
            />
          )}
        </div>
      )}

      {trackedUsers.length === 0 && (
        <div className="text-center py-6">
          <RouteIcon className="w-8 h-8 text-neutral-300 dark:text-neutral-600 mx-auto mb-2" />
          <p className="text-xs text-neutral-400">
            Search and add customers to track their journey across the event.
          </p>
        </div>
      )}
    </div>
  )
}

// ────────────────────── Map Canvas ──────────────────────
function MapCanvas({
  eventId,
  mapConfig,
  vendorEvents: _vendorEvents,
  mode,
  mapViz,
  selectedVendorId,
  onVendorPlaced,
  drawingBoundary,
  boundaryPoints,
  onAddBoundaryPoint,
  onCloseBoundary,
  venueAddress,
  isDark,
  overlayOpacity,
  overlayRotation,
  overlayScale,
  overlayOffsetX,
  overlayOffsetY,
}: {
  eventId: number
  mapConfig: MapConfig | null
  vendorEvents: VendorEvent[]
  mode: ViewMode
  mapViz: MapVisualization
  selectedVendorId: number | null
  onVendorPlaced: () => void
  drawingBoundary: boolean
  boundaryPoints: [number, number][]
  onAddBoundaryPoint: (point: [number, number]) => void
  onCloseBoundary: () => void
  venueAddress?: string
  isDark: boolean
  overlayOpacity: number
  overlayRotation: number
  overlayScale: number
  overlayOffsetX: number
  overlayOffsetY: number
}) {
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const markersRef = useRef<mapboxgl.Marker[]>([])
  const vizMarkersRef = useRef<mapboxgl.Marker[]>([])
  const boundaryMarkersRef = useRef<mapboxgl.Marker[]>([])
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const updateMapConfig = useUpdateMapConfig()

  const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN

  // Safe helpers for map layer/source operations
  const safeRemoveLayer = (map: mapboxgl.Map, id: string) => {
    try { if (map.getStyle() && map.getLayer(id)) map.removeLayer(id) } catch (_) {}
  }
  const safeRemoveSource = (map: mapboxgl.Map, id: string) => {
    try { if (map.getStyle() && map.getSource(id)) map.removeSource(id) } catch (_) {}
  }
  // @ts-expect-error reserved for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const safeHasSource = (map: mapboxgl.Map, id: string): boolean => {
    try { return !!(map.getStyle() && map.getSource(id)) } catch (_) { return false }
  }

  // Derive bounding box from site boundary polygon
  // Returns [[swLat, swLng], [neLat, neLng]] or null
  const boundaryBounds = useMemo((): [[number, number], [number, number]] | null => {
    const coords = mapConfig?.map_boundary?.coordinates?.[0]
    if (!coords || coords.length < 3) return null
    let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity
    coords.forEach(([lng, lat]: number[]) => {
      if (lng < minLng) minLng = lng
      if (lng > maxLng) maxLng = lng
      if (lat < minLat) minLat = lat
      if (lat > maxLat) maxLat = lat
    })
    return [[minLat, minLng], [maxLat, maxLng]]
  }, [mapConfig?.map_boundary])

  // Default center priority: boundary center > map config center > NYC fallback
  // (Venue address geocoding is handled separately after map loads)
  // Synchronously-known center: boundary midpoint or the saved map center.
  const knownCenter = useMemo<LngLat | null>(() => {
    if (boundaryBounds) {
      const cLng = (boundaryBounds[0][1] + boundaryBounds[1][1]) / 2
      const cLat = (boundaryBounds[0][0] + boundaryBounds[1][0]) / 2
      return [cLng, cLat]
    }
    if (mapConfig?.map_center_longitude && mapConfig?.map_center_latitude) {
      return [mapConfig.map_center_longitude, mapConfig.map_center_latitude]
    }
    return null
  }, [boundaryBounds, mapConfig?.map_center_longitude, mapConfig?.map_center_latitude])

  // Resolve BEFORE constructing the map — no fallback-city flash, no
  // cross-continent flyTo. Geocode result is cached per address.
  const [resolvedCenter, setResolvedCenter] = useState<LngLat | null>(
    () => knownCenter ?? cachedVenueCenter(venueAddress),
  )
  useEffect(() => {
    if (knownCenter && !resolvedCenter) setResolvedCenter(knownCenter)
  }, [knownCenter, resolvedCenter])
  useEffect(() => {
    if (resolvedCenter || !mapboxToken) return
    if (!venueAddress) {
      setResolvedCenter(FALLBACK_MAP_CENTER)
      return
    }
    let cancelled = false
    geocodeVenueCenter(venueAddress, mapboxToken).then((center) => {
      if (!cancelled) setResolvedCenter(center ?? FALLBACK_MAP_CENTER)
    })
    return () => { cancelled = true }
  }, [resolvedCenter, venueAddress, mapboxToken])

  const defaultZoom = mapConfig?.map_zoom_level ?? (boundaryBounds ? 16 : 15)

  // Ref for the HTML image overlay element
  const overlayImgRef = useRef<HTMLImageElement | null>(null)

  const mapStyleUrl = isDark
    ? 'mapbox://styles/mapbox/dark-v11'
    : 'mapbox://styles/mapbox/light-v11'

  // Refs that hold the latest "add layers" functions so the style.load listener can call them
  const addBoundaryRef = useRef<(() => void) | null>(null)
  const addHeatmapRef = useRef<(() => void) | null>(null)
  const addJourneyRef = useRef<(() => void) | null>(null)
  const addVendorMarkersRef = useRef<(() => void) | null>(null)

  // Initialize map (only recreated when token or boundary changes — style swaps happen separately)
  useEffect(() => {
    if (!mapContainerRef.current || !mapboxToken || !resolvedCenter) return

    mapboxgl.accessToken = mapboxToken

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: mapStyleUrl,
      center: resolvedCenter,
      zoom: defaultZoom,
    })

    map.addControl(new mapboxgl.NavigationControl(), 'top-right')
    mapRef.current = map

    map.on('load', () => {
      // Auto-fit map to site boundary polygon
      if (boundaryBounds) {
        const fitBounds = new mapboxgl.LngLatBounds()
        fitBounds.extend([boundaryBounds[0][1], boundaryBounds[0][0]]) // SW [lng, lat]
        fitBounds.extend([boundaryBounds[1][1], boundaryBounds[1][0]]) // NE [lng, lat]
        map.fitBounds(fitBounds, { padding: 20, maxZoom: 19 })
      }
    })

    // Persistent style.load listener — re-adds all custom layers after any setStyle() call
    map.on('style.load', () => {
      addBoundaryRef.current?.()
      addHeatmapRef.current?.()
      addJourneyRef.current?.()
      addVendorMarkersRef.current?.()
    })

    return () => {
      map.remove()
      mapRef.current = null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapboxToken, boundaryBounds, resolvedCenter])

  // Theme swap — only calls setStyle when the style URL actually changes
  const prevStyleRef = useRef(mapStyleUrl)
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (prevStyleRef.current === mapStyleUrl) return
    prevStyleRef.current = mapStyleUrl
    map.setStyle(mapStyleUrl)
  }, [mapStyleUrl])

  // ── Resize map when container size changes (e.g. sidebar toggle) ──
  useEffect(() => {
    const container = mapContainerRef.current
    const map = mapRef.current
    if (!container || !map) return

    const observer = new ResizeObserver(() => {
      map.resize()
    })
    observer.observe(container)
    return () => observer.disconnect()
  })


  // ── Image overlay as positioned HTML <img> (avoids CORS entirely) ──
  // Store updatePosition in a ref so the style-update effect can trigger it
  const overlayUpdatePositionRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    // Remove previous overlay
    if (overlayImgRef.current) {
      overlayImgRef.current.remove()
      overlayImgRef.current = null
    }
    overlayUpdatePositionRef.current = null

    if (!mapConfig?.map_overlay_image || !boundaryBounds) return

    // Create an <img> element positioned absolutely over the map canvas
    const img = document.createElement('img')
    img.src = mapConfig.map_overlay_image
    img.style.position = 'absolute'
    img.style.pointerEvents = 'none'
    img.style.opacity = String(overlayOpacity)
    img.style.zIndex = '1' // Above map tiles, below markers/controls
    img.style.transformOrigin = 'center center'
    overlayImgRef.current = img
    map.getCanvasContainer().appendChild(img)

    // Project geographic bounds to pixel position
    const swLng = boundaryBounds[0][1]
    const swLat = boundaryBounds[0][0]
    const neLng = boundaryBounds[1][1]
    const neLat = boundaryBounds[1][0]

    const updatePosition = () => {
      if (!overlayImgRef.current) return
      const topLeft = map.project([swLng, neLat])
      const bottomRight = map.project([neLng, swLat])
      const w = bottomRight.x - topLeft.x
      const h = bottomRight.y - topLeft.y
      // Apply offset as percentage of boundary size
      const ox = (overlayOffsetX / 100) * w
      const oy = (overlayOffsetY / 100) * h
      overlayImgRef.current.style.left = (topLeft.x + ox) + 'px'
      overlayImgRef.current.style.top = (topLeft.y + oy) + 'px'
      overlayImgRef.current.style.width = w + 'px'
      overlayImgRef.current.style.height = h + 'px'
      overlayImgRef.current.style.transform = `rotate(${overlayRotation}deg) scale(${overlayScale})`
    }

    overlayUpdatePositionRef.current = updatePosition

    // Position immediately and on every map movement
    updatePosition()
    map.on('move', updatePosition)
    map.on('resize', updatePosition)

    return () => {
      map.off('move', updatePosition)
      map.off('resize', updatePosition)
      if (overlayImgRef.current) {
        overlayImgRef.current.remove()
        overlayImgRef.current = null
      }
      overlayUpdatePositionRef.current = null
    }
  }, [mapConfig?.map_overlay_image, boundaryBounds, overlayOpacity, overlayRotation, overlayScale, overlayOffsetX, overlayOffsetY])

  // ── Render vendor markers — store in ref for style.load re-add ──
  useEffect(() => {
    const map = mapRef.current
    if (!map || !boundaryBounds) return

    const addMarkers = () => {
      if (!mapRef.current || !boundaryBounds) return
      const m = mapRef.current

      // Clear existing markers
      markersRef.current.forEach((mk) => mk.remove())
      markersRef.current = []

      const placedVendors = (mapConfig?.vendor_locations || []).filter((v) => v.x != null && v.y != null)

      // Build number map matching sidebar order (sort by vendor_name for consistency)
      const sorted = [...placedVendors].sort((a, b) => (a.vendor_name || '').localeCompare(b.vendor_name || ''))
      const numberMap = new Map<number, number>()
      sorted.forEach((v, i) => numberMap.set(v.vendor_event_id, i + 1))

      placedVendors.forEach((vendor) => {
        // Convert percentage coordinates to lat/lng using boundary bounding box
        const lng = boundaryBounds[0][1] + (vendor.x / 100) * (boundaryBounds[1][1] - boundaryBounds[0][1])
        const lat = boundaryBounds[1][0] - (vendor.y / 100) * (boundaryBounds[1][0] - boundaryBounds[0][0])

        const num = numberMap.get(vendor.vendor_event_id) ?? 0

        const el = document.createElement('div')
        el.className = 'vendor-pin'
        el.style.cssText = `
          width: 24px; height: 24px; border-radius: 50%;
          background: #3b82f6; border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          box-sizing: border-box;
          cursor: pointer; color: white; font-size: 9px;
          font-weight: 700; line-height: 20px;
          text-align: center; user-select: none;
        `
        el.textContent = String(num)
        el.title = `#${num} ${vendor.vendor_name} (${vendor.booth})`

        const popup = new mapboxgl.Popup({ offset: 15, closeButton: false, closeOnClick: false })
          .setHTML(
            `<div style="padding:4px"><strong>#${num} ${vendor.vendor_name}</strong><br/><small>${vendor.booth}</small></div>`
          )

        const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
          .setLngLat([lng, lat])
          .setPopup(popup)
          .addTo(m)

        // Show popup on hover instead of click
        el.addEventListener('mouseenter', () => marker.togglePopup())
        el.addEventListener('mouseleave', () => marker.togglePopup())

        markersRef.current.push(marker)
      })
    }

    addVendorMarkersRef.current = addMarkers
    addMarkers()

    return () => {
      addVendorMarkersRef.current = null
      markersRef.current.forEach((mk) => mk.remove())
      markersRef.current = []
    }
  }, [mapConfig?.vendor_locations, boundaryBounds])

  // ── Native Mapbox heatmap layer for heatmap / timeline modes — store in ref for style.load ──
  useEffect(() => {
    const map = mapRef.current
    if (!map || !boundaryBounds) return

    const cleanup = () => {
      if (!mapRef.current) return
      const m = mapRef.current
      safeRemoveLayer(m, 'viz-heatmap')
      safeRemoveLayer(m, 'viz-heatmap-points')
      safeRemoveSource(m, 'viz-heatmap-source')
      // Restore regular vendor markers
      markersRef.current.forEach((mk) => mk.getElement().style.display = '')
    }

    // Determine which data to show
    let vizLocations: Array<{ vendor_event_id: number; x: number; y: number; intensity: number; label?: string }> = []

    if (mode === 'heatmap' && mapViz.heatmapLocations) {
      vizLocations = mapViz.heatmapLocations.map((loc) => ({
        vendor_event_id: loc.vendor_event_id,
        x: loc.x,
        y: loc.y,
        intensity: loc.intensity,
        label: loc.vendor_name,
      }))
    } else if (mode === 'timeline' && mapViz.timelineFrame) {
      vizLocations = mapViz.timelineFrame.map((loc) => ({
        vendor_event_id: loc.vendor_event_id,
        x: loc.x,
        y: loc.y,
        intensity: loc.intensity,
      }))
    }

    // No data or wrong mode - clean up and exit
    if (vizLocations.length === 0) {
      addHeatmapRef.current = null
      cleanup()
      return
    }

    // Build GeoJSON features from vendor locations
    const features = vizLocations
      .filter((loc) => loc.x != null && loc.y != null)
      .map((loc) => {
        const lng = boundaryBounds[0][1] + (loc.x / 100) * (boundaryBounds[1][1] - boundaryBounds[0][1])
        const lat = boundaryBounds[1][0] - (loc.y / 100) * (boundaryBounds[1][0] - boundaryBounds[0][0])
        return {
          type: 'Feature' as const,
          properties: {
            intensity: loc.intensity,
            label: loc.label || `Vendor ${loc.vendor_event_id}`,
          },
          geometry: {
            type: 'Point' as const,
            coordinates: [lng, lat],
          },
        }
      })

    const geojsonData = { type: 'FeatureCollection' as const, features }

    // Dim regular vendor markers (but keep numbers visible for reference)
    markersRef.current.forEach((mk) => {
      mk.getElement().style.opacity = '0.3'
    })

    const addHeatmapLayer = () => {
      if (!mapRef.current) return
      const m = mapRef.current

      safeRemoveLayer(m, 'viz-heatmap')
      safeRemoveLayer(m, 'viz-heatmap-points')
      safeRemoveSource(m, 'viz-heatmap-source')

      m.addSource('viz-heatmap-source', {
        type: 'geojson',
        data: geojsonData as any,
      })

      // Heatmap layer - smooth gradient blobs
      m.addLayer({
        id: 'viz-heatmap',
        type: 'heatmap',
        source: 'viz-heatmap-source',
        paint: {
          // Weight each point by its intensity (0-1)
          'heatmap-weight': [
            'interpolate', ['linear'], ['get', 'intensity'],
            0, 0,
            1, 1,
          ],
          // Intensity multiplier
          'heatmap-intensity': [
            'interpolate', ['linear'], ['zoom'],
            10, 1,
            18, 3,
          ],
          // Color ramp: transparent -> blue -> cyan -> yellow -> red
          'heatmap-color': [
            'interpolate', ['linear'], ['heatmap-density'],
            0, 'rgba(33,102,172,0)',
            0.15, 'rgba(103,169,207,0.6)',
            0.3, 'rgba(209,229,240,0.7)',
            0.5, 'rgba(253,219,199,0.8)',
            0.7, 'rgba(239,138,98,0.85)',
            1, 'rgba(178,24,43,0.9)',
          ],
          // Radius of influence for each point
          'heatmap-radius': [
            'interpolate', ['linear'], ['zoom'],
            10, 30,
            14, 50,
            18, 80,
          ],
          'heatmap-opacity': 0.75,
        },
      })

      // Circle point layer on top - visible at closer zoom
      m.addLayer({
        id: 'viz-heatmap-points',
        type: 'circle',
        source: 'viz-heatmap-source',
        paint: {
          'circle-radius': [
            'interpolate', ['linear'], ['get', 'intensity'],
            0, 4,
            0.5, 8,
            1, 14,
          ],
          'circle-color': [
            'interpolate', ['linear'], ['get', 'intensity'],
            0, 'rgb(103,169,207)',
            0.3, 'rgb(253,219,199)',
            0.6, 'rgb(239,138,98)',
            1, 'rgb(178,24,43)',
          ],
          'circle-stroke-color': 'white',
          'circle-stroke-width': 1.5,
          'circle-opacity': 0.9,
        },
      })
    }

    addHeatmapRef.current = addHeatmapLayer

    if (map.isStyleLoaded()) {
      addHeatmapLayer()
    } else {
      map.once('load', addHeatmapLayer)
    }

    return () => {
      addHeatmapRef.current = null
      cleanup()
    }
  }, [mode, mapViz.heatmapLocations, mapViz.timelineFrame, boundaryBounds])

  // ── Journey path lines — store in ref for style.load re-add ──
  useEffect(() => {
    const map = mapRef.current
    if (!map || !boundaryBounds) return

    const playbackStep = mapViz.journeyPlaybackStep // undefined = show all, 0+ = show up to step

    // Clean up previous journey layers
    const cleanupJourneys = () => {
      if (!mapRef.current) return
      const m = mapRef.current
      for (let i = 0; i < 10; i++) {
        safeRemoveLayer(m, `journey-line-${i}`)
        safeRemoveLayer(m, `journey-points-${i}`)
        safeRemoveSource(m, `journey-source-${i}`)
      }
    }

    cleanupJourneys()
    vizMarkersRef.current.forEach((mk) => mk.remove())
    vizMarkersRef.current = []

    if (mode !== 'journey' || !mapViz.journeys || mapViz.journeys.length === 0) {
      addJourneyRef.current = null
      markersRef.current.forEach((mk) => { mk.getElement().style.opacity = ''; mk.getElement().style.display = '' })
      return
    }

    // Dim regular vendor markers when showing journeys
    markersRef.current.forEach((mk) => mk.getElement().style.opacity = '0.2')

    const addLayers = () => {
      if (!mapRef.current || !boundaryBounds) return
      const m = mapRef.current

      // Clean existing journey layers before re-adding
      for (let i = 0; i < 10; i++) {
        safeRemoveLayer(m, `journey-line-${i}`)
        safeRemoveLayer(m, `journey-points-${i}`)
        safeRemoveSource(m, `journey-source-${i}`)
      }
      vizMarkersRef.current.forEach((mk) => mk.remove())
      vizMarkersRef.current = []

      mapViz.journeys!.forEach((journey, i) => {
        // Determine which steps to show
        const visibleSteps = playbackStep != null
          ? journey.path.slice(0, playbackStep + 1)
          : journey.path

        if (visibleSteps.length < 1) return

        const coordinates = visibleSteps.map((step) => {
          const lng = boundaryBounds![0][1] + (step.x / 100) * (boundaryBounds![1][1] - boundaryBounds![0][1])
          const lat = boundaryBounds![1][0] - (step.y / 100) * (boundaryBounds![1][0] - boundaryBounds![0][0])
          return [lng, lat]
        })

        const sourceId = `journey-source-${i}`
        const lineId = `journey-line-${i}`

        // Only draw a line if we have 2+ points
        if (coordinates.length >= 2) {
          m.addSource(sourceId, {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: { type: 'LineString', coordinates },
            },
          })

          m.addLayer({
            id: lineId,
            type: 'line',
            source: sourceId,
            paint: {
              'line-color': journey.color,
              'line-width': 3,
              'line-opacity': 0.8,
              'line-dasharray': [2, 1],
            },
          })
        }

        // Add numbered circle markers at each visible step
        visibleSteps.forEach((step, stepIdx) => {
          const lng = boundaryBounds![0][1] + (step.x / 100) * (boundaryBounds![1][1] - boundaryBounds![0][1])
          const lat = boundaryBounds![1][0] - (step.y / 100) * (boundaryBounds![1][0] - boundaryBounds![0][0])

          const isCurrentStep = playbackStep != null && stepIdx === playbackStep
          const size = isCurrentStep ? 26 : 20

          const el = document.createElement('div')
          el.style.cssText = `
            width: ${size}px; height: ${size}px; border-radius: 50%;
            background: ${journey.color}; border: 2px solid white;
            box-shadow: 0 1px 4px rgba(0,0,0,0.3)${isCurrentStep ? ', 0 0 8px ' + journey.color : ''};
            display: flex; align-items: center; justify-content: center;
            color: white; font-size: ${isCurrentStep ? 11 : 9}px; font-weight: 700; line-height: 1;
            transition: all 0.2s ease;
          `
          el.textContent = String(stepIdx + 1)

          const popup = new mapboxgl.Popup({ offset: 14, closeButton: false, closeOnClick: false })
            .setHTML(
              `<div style="padding:4px;text-align:center">
                <strong>Step ${stepIdx + 1}</strong><br/>
                <small>${step.amount > 0 ? '$' + step.amount.toFixed(2) : '--'}</small><br/>
                <small>${new Date(step.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</small>
              </div>`
            )

          const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
            .setLngLat([lng, lat])
            .setPopup(popup)
            .addTo(m)

          el.addEventListener('mouseenter', () => marker.togglePopup())
          el.addEventListener('mouseleave', () => marker.togglePopup())

          vizMarkersRef.current.push(marker)
        })
      })
    }

    addJourneyRef.current = addLayers

    if (map.isStyleLoaded()) {
      addLayers()
    } else {
      map.once('load', addLayers)
    }

    return () => {
      addJourneyRef.current = null
      cleanupJourneys()
      vizMarkersRef.current.forEach((mk) => mk.remove())
      vizMarkersRef.current = []
      markersRef.current.forEach((mk) => { mk.getElement().style.opacity = ''; mk.getElement().style.display = '' })
    }
  }, [mode, mapViz.journeys, mapViz.journeyPlaybackStep, boundaryBounds])

  // ── Display saved boundary polygon — store in ref for style.load re-add ──
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    const addBoundaryLayer = () => {
      if (!mapRef.current) return
      const m = mapRef.current
      // Remove existing
      safeRemoveLayer(m, 'saved-boundary-line')
      safeRemoveLayer(m, 'saved-boundary-fill')
      safeRemoveSource(m, 'saved-boundary')

      if (!mapConfig?.map_boundary || drawingBoundary) return

      const coords = (mapConfig.map_boundary as any).coordinates
      if (!coords || !coords[0] || coords[0].length < 3) return

      m.addSource('saved-boundary', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: mapConfig.map_boundary as any,
        },
      })

      m.addLayer({
        id: 'saved-boundary-fill',
        type: 'fill',
        source: 'saved-boundary',
        paint: {
          'fill-color': '#8b5cf6',
          'fill-opacity': 0.08,
        },
      })

      m.addLayer({
        id: 'saved-boundary-line',
        type: 'line',
        source: 'saved-boundary',
        paint: {
          'line-color': '#8b5cf6',
          'line-width': 2,
          'line-dasharray': [2, 2],
        },
      })
    }

    addBoundaryRef.current = addBoundaryLayer

    if (map.isStyleLoaded()) {
      addBoundaryLayer()
    } else {
      map.on('load', addBoundaryLayer)
    }

    return () => {
      addBoundaryRef.current = null
      safeRemoveLayer(map, 'saved-boundary-line')
      safeRemoveLayer(map, 'saved-boundary-fill')
      safeRemoveSource(map, 'saved-boundary')
    }
  }, [mapConfig?.map_boundary, drawingBoundary])

  // ── Boundary drawing: render in-progress polygon + handle clicks ──
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    // Clean up drawing layers
    const cleanup = () => {
      safeRemoveLayer(map, 'drawing-boundary-line')
      safeRemoveLayer(map, 'drawing-boundary-fill')
      safeRemoveSource(map, 'drawing-boundary')
      boundaryMarkersRef.current.forEach((m) => m.remove())
      boundaryMarkersRef.current = []
    }

    if (!drawingBoundary) {
      cleanup()
      map.getCanvas().style.cursor = ''
      return
    }

    map.getCanvas().style.cursor = 'crosshair'

    // Render current points as markers
    cleanup()
    boundaryPoints.forEach((pt, i) => {
      const el = document.createElement('div')
      const isFirst = i === 0
      el.style.cssText = `
        width: ${isFirst ? '14px' : '10px'}; height: ${isFirst ? '14px' : '10px'};
        border-radius: 50%;
        background: ${isFirst ? '#8b5cf6' : '#a78bfa'}; border: 2px solid white;
        box-shadow: 0 1px 4px rgba(0,0,0,0.3);
        cursor: ${isFirst && boundaryPoints.length >= 3 ? 'pointer' : 'default'};
      `
      if (isFirst && boundaryPoints.length >= 3) {
        el.title = 'Click to close boundary'
      }

      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat(pt)
        .addTo(map)

      boundaryMarkersRef.current.push(marker)
    })

    // Draw lines between points
    const renderDrawingLine = () => {
      safeRemoveLayer(map, 'drawing-boundary-line')
      safeRemoveLayer(map, 'drawing-boundary-fill')
      safeRemoveSource(map, 'drawing-boundary')

      if (boundaryPoints.length < 2) return

      const coordinates = [...boundaryPoints]
      // Close the polygon visually if 3+ points
      if (boundaryPoints.length >= 3) {
        coordinates.push(boundaryPoints[0])
      }

      map.addSource('drawing-boundary', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: boundaryPoints.length >= 3
            ? { type: 'Polygon' as const, coordinates: [coordinates] }
            : { type: 'LineString' as const, coordinates },
        },
      })

      if (boundaryPoints.length >= 3) {
        map.addLayer({
          id: 'drawing-boundary-fill',
          type: 'fill',
          source: 'drawing-boundary',
          paint: {
            'fill-color': '#8b5cf6',
            'fill-opacity': 0.1,
          },
        })
      }

      map.addLayer({
        id: 'drawing-boundary-line',
        type: 'line',
        source: 'drawing-boundary',
        paint: {
          'line-color': '#8b5cf6',
          'line-width': 2,
        },
      })
    }

    if (map.isStyleLoaded()) {
      renderDrawingLine()
    }

    // Click handler for adding points
    const handleClick = (e: mapboxgl.MapMouseEvent) => {
      const clickLngLat: [number, number] = [e.lngLat.lng, e.lngLat.lat]

      // Check if clicking near the first point to close
      if (boundaryPoints.length >= 3) {
        const first = boundaryPoints[0]
        const pixel = map.project(new mapboxgl.LngLat(first[0], first[1]))
        const clickPixel = map.project(e.lngLat)
        const dist = Math.sqrt((pixel.x - clickPixel.x) ** 2 + (pixel.y - clickPixel.y) ** 2)

        if (dist < 15) {
          // Close the polygon - save to API
          const closedCoords = [...boundaryPoints, boundaryPoints[0]]
          const boundary = {
            type: 'Polygon',
            coordinates: [closedCoords],
          }
          updateMapConfig.mutateAsync({
            eventId,
            data: { map_boundary: boundary } as Partial<MapConfig>,
          }).then(() => {
            onCloseBoundary()
          })
          return
        }
      }

      onAddBoundaryPoint(clickLngLat)
    }

    map.on('click', handleClick)

    return () => {
      map.off('click', handleClick)
      map.getCanvas().style.cursor = ''
    }
  }, [drawingBoundary, boundaryPoints, eventId])

  // ── Click-to-place vendor handler ──
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    // Set cursor style (don't override if drawing boundary)
    const canvas = map.getCanvas()
    if (!drawingBoundary) {
      if (selectedVendorId && mode === 'setup') {
        canvas.style.cursor = 'crosshair'
      } else {
        canvas.style.cursor = ''
      }
    }

    if (!selectedVendorId || mode !== 'setup' || !boundaryBounds || !token || drawingBoundary) return

    const handleClick = async (e: mapboxgl.MapMouseEvent) => {
      const lngLat = e.lngLat

      // Convert lng/lat to percentage coordinates relative to boundary bounding box
      const x = ((lngLat.lng - boundaryBounds[0][1]) / (boundaryBounds[1][1] - boundaryBounds[0][1])) * 100
      const y = ((boundaryBounds[1][0] - lngLat.lat) / (boundaryBounds[1][0] - boundaryBounds[0][0])) * 100

      const clampedX = Math.max(0, Math.min(100, x))
      const clampedY = Math.max(0, Math.min(100, y))

      try {
        await apiClient.updateVendorEvent(token, selectedVendorId, { x: clampedX, y: clampedY } as any)
        // Refresh map config to show new pin
        queryClient.invalidateQueries({ queryKey: queryKeys.mapConfig(eventId) })
        onVendorPlaced()
      } catch (err) {
        console.error('Failed to place vendor:', err)
      }
    }

    map.on('click', handleClick)
    return () => {
      map.off('click', handleClick)
      canvas.style.cursor = ''
    }
  }, [selectedVendorId, mode, boundaryBounds, token, eventId])

  if (!mapboxToken) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-neutral-50 dark:bg-neutral-900">
        <MapIcon className="w-12 h-12 text-neutral-300 dark:text-neutral-600 mb-3" />
        <h3 className="text-lg font-semibold text-neutral-700 dark:text-neutral-300 mb-1">Mapbox Token Required</h3>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center max-w-sm">
          Add <code className="px-1 py-0.5 rounded bg-neutral-200 dark:bg-neutral-700 text-xs">VITE_MAPBOX_TOKEN</code> to your <code className="px-1 py-0.5 rounded bg-neutral-200 dark:bg-neutral-700 text-xs">.env</code> file to enable the map.
        </p>
      </div>
    )
  }

  return (
    <div className="relative w-full h-full">
      <div
        ref={mapContainerRef}
        className="w-full h-full"
      />
      {!resolvedCenter && (
        <div className="absolute inset-0 flex items-center justify-center bg-neutral-100 dark:bg-neutral-950">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      )}
    </div>
  )
}

// ────────────────────── Helpers ──────────────────────
function intensityToColor(intensity: number): string {
  // Cold (blue) -> Warm (yellow) -> Hot (red)
  if (intensity < 0.5) {
    const t = intensity * 2
    const r = Math.round(59 + t * (245 - 59))
    const g = Math.round(130 + t * (158 - 130))
    const b = Math.round(246 + t * (11 - 246))
    return `rgb(${r},${g},${b})`
  } else {
    const t = (intensity - 0.5) * 2
    const r = Math.round(245 + t * (239 - 245))
    const g = Math.round(158 + t * (68 - 158))
    const b = Math.round(11 + t * (68 - 11))
    return `rgb(${r},${g},${b})`
  }
}
